import type { TFunction } from 'i18next';
import type { PathMarker, PathMarkerType, Waypoint } from '../../Components/PathBuilder/pathBuilderTypes';
import { PATH_MARKER_TYPES } from '../../Components/PathBuilder/pathBuilderTypes';
import { roundCoordinateMeters } from '../fieldSelection';
import { generateSegmentPath, type PathMeasureLike } from './path-builder-helpers';

const STRAIGHT_SEGMENT_PATH_RE = /^M\s*([-\d.]+),([-\d.]+)\s+L\s*([-\d.]+),([-\d.]+)$/;

const MARKER_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Generate the next default marker name (marker_A, marker_B, …) that is not
 * already used by any existing marker on the path.
 */
export function generateDefaultMarkerName(existing: PathMarker[]): string {
  const usedNames = new Set(existing.map((m) => m.name));
  for (const letter of MARKER_LETTERS) {
    const candidate = `marker_${letter}`;
    if (!usedNames.has(candidate)) return candidate;
  }
  // Fallback: append a numeric suffix after the alphabet is exhausted
  let n = 1;
  while (true) {
    const candidate = `marker_${n}`;
    if (!usedNames.has(candidate)) return candidate;
    n++;
  }
}

/**
 * Derive the display letter from a marker name, e.g. "marker_A" → "A".
 * Falls back to the first two uppercased characters for non-standard names.
 */
export function getMarkerDisplayLetter(name: string): string {
  const match = /^marker_([A-Z])$/i.exec(name);
  if (match) return match[1].toUpperCase();
  return name.toUpperCase().slice(0, 2);
}

/** Localized label for a marker's placement type (percent, distance from start/end). */
export function formatMarkerPlacementLabel(marker: PathMarker, t: TFunction): string {
  switch (marker.type) {
    case PATH_MARKER_TYPES.SEGMENT_PERCENT:
      return t('pathBuilderPage.contextMenu.markerType.segmentPercent', '% of segment');
    case PATH_MARKER_TYPES.DISTANCE_FROM_START:
      return t('pathBuilderPage.contextMenu.markerType.distanceFromStart', 'Dist. from start (m)');
    case PATH_MARKER_TYPES.DISTANCE_FROM_END:
      return t('pathBuilderPage.contextMenu.markerType.distanceFromEnd', 'Dist. from end (m)');
    default:
      return marker.type;
  }
}

/**
 * Compute the segment_percent value (0–100) for a click point on a curved
 * path segment by querying the SVG element for that segment.
 *
 * When the segment SVG element is not available (e.g. in tests), falls back
 * to straight-line interpolation.
 */
export function computeSegmentPercentFromClick(
  clickX: number,
  clickY: number,
  segmentIndex: number,
  svg: SVGSVGElement | null,
  pathId: string
): number {
  if (svg) {
    const segmentPaths = svg.querySelectorAll<SVGPathElement>('path[data-path-builder-segment]');
    for (const pathEl of segmentPaths) {
      if (pathEl.dataset.pathId !== pathId) continue;
      if (String(pathEl.dataset.segmentIndex) !== String(segmentIndex)) continue;
      const totalLength = pathEl.getTotalLength();
      if (totalLength <= 0) return 0;

      // Binary-search the closest point on the path to the click
      let lo = 0;
      let hi = totalLength;
      for (let i = 0; i < 16; i++) {
        const mid = (lo + hi) / 2;
        const midPoint = pathEl.getPointAtLength(mid);
        const beforeMid = pathEl.getPointAtLength(Math.max(0, mid - 0.5));
        const afterMid = pathEl.getPointAtLength(Math.min(totalLength, mid + 0.5));
        const dBefore = Math.hypot(clickX - beforeMid.x, clickY - beforeMid.y);
        const dAfter = Math.hypot(clickX - afterMid.x, clickY - afterMid.y);
        void midPoint;
        if (dBefore < dAfter) {
          hi = mid;
        } else {
          lo = mid;
        }
      }
      const bestLength = (lo + hi) / 2;
      return Math.round((bestLength / totalLength) * 100);
    }
  }

  return 50;
}

function createSegmentMeasurer(pathD: string | null): PathMeasureLike {
  if (!pathD) {
    return { getTotalLength: () => 0, getPointAtLength: () => ({ x: 0, y: 0 }) };
  }

  const straight = STRAIGHT_SEGMENT_PATH_RE.exec(pathD.trim());
  if (straight) {
    const x1 = Number(straight[1]);
    const y1 = Number(straight[2]);
    const x2 = Number(straight[3]);
    const y2 = Number(straight[4]);
    const length = Math.hypot(x2 - x1, y2 - y1);
    return {
      getTotalLength: () => length,
      getPointAtLength: (distance: number) => {
        const t = length <= 0 ? 0 : Math.max(0, Math.min(1, distance / length));
        return { x: x1 + (x2 - x1) * t, y: y1 + (y2 - y1) * t };
      },
    };
  }

  if (typeof document !== 'undefined') {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', pathD);
    return path;
  }

  return { getTotalLength: () => 0, getPointAtLength: () => ({ x: 0, y: 0 }) };
}

function measureWaypointSegments(waypoints: Waypoint[]): PathMeasureLike[] {
  if (waypoints.length < 2) return [];
  return waypoints.slice(0, -1).map((_, segmentIndex) => createSegmentMeasurer(generateSegmentPath(waypoints, segmentIndex)));
}

function markerLocalDistance(marker: PathMarker, segment: PathMeasureLike): number | null {
  const segmentLength = segment.getTotalLength();
  if (segmentLength <= 0) return null;

  if (marker.type === PATH_MARKER_TYPES.SEGMENT_PERCENT) {
    return (marker.value / 100) * segmentLength;
  }
  if (marker.type === PATH_MARKER_TYPES.DISTANCE_FROM_START) {
    return marker.value;
  }
  return segmentLength - marker.value;
}

function markerValueFromLocalDistance(localDistance: number, segmentLength: number, type: PathMarkerType): number {
  const clamped = Math.max(0, Math.min(segmentLength, localDistance));

  if (type === PATH_MARKER_TYPES.SEGMENT_PERCENT) {
    return segmentLength <= 0 ? 0 : Math.round((clamped / segmentLength) * 100);
  }
  if (type === PATH_MARKER_TYPES.DISTANCE_FROM_START) {
    return roundCoordinateMeters(clamped);
  }
  return roundCoordinateMeters(Math.max(0, segmentLength - clamped));
}

function distanceFromSegmentStart(marker: PathMarker, segment: PathMeasureLike): number {
  return markerLocalDistance(marker, segment) ?? 0;
}

function distanceFromSegmentEnd(marker: PathMarker, segment: PathMeasureLike): number {
  const segmentLength = segment.getTotalLength();
  return Math.max(0, segmentLength - distanceFromSegmentStart(marker, segment));
}

function markerFromAnchoredDistance(
  anchor: 'start' | 'end',
  anchorDistance: number,
  segment: PathMeasureLike,
  type: PathMarkerType
): Pick<PathMarker, 'type' | 'value'> {
  const segmentLength = segment.getTotalLength();
  const clampedAnchor = Math.max(0, anchorDistance);
  const localDistance =
    anchor === 'start' ? Math.min(clampedAnchor, segmentLength) : Math.max(0, segmentLength - Math.min(clampedAnchor, segmentLength));

  return {
    type,
    value: markerValueFromLocalDistance(localDistance, segmentLength, type),
  };
}

function remapOntoSegment(
  marker: PathMarker,
  anchor: 'start' | 'end',
  anchorDistance: number,
  segmentIndex: number,
  targetSegment: PathMeasureLike
): PathMarker {
  const placement = markerFromAnchoredDistance(anchor, anchorDistance, targetSegment, marker.type);
  return { ...marker, segmentIndex, ...placement };
}

function remapMarkerAfterWaypointRemoval(
  marker: PathMarker,
  removeAt: number,
  oldN: number,
  oldSegments: PathMeasureLike[],
  newSegments: PathMeasureLike[]
): PathMarker {
  const seg = marker.segmentIndex;
  const oldSegment = oldSegments[seg];
  if (!oldSegment) return marker;

  if (removeAt > 0 && seg < removeAt - 1) {
    return marker;
  }

  if ((removeAt < oldN - 1 && seg > removeAt) || (removeAt === 0 && seg > 0)) {
    return { ...marker, segmentIndex: seg - 1 };
  }

  if (removeAt > 0 && removeAt < oldN - 1) {
    const mergedSegment = newSegments[removeAt - 1];
    if (!mergedSegment) return marker;

    if (seg === removeAt - 1) {
      return remapOntoSegment(marker, 'start', distanceFromSegmentStart(marker, oldSegment), removeAt - 1, mergedSegment);
    }
    if (seg === removeAt) {
      return remapOntoSegment(marker, 'end', distanceFromSegmentEnd(marker, oldSegment), removeAt - 1, mergedSegment);
    }
  }

  if (removeAt === oldN - 1 && seg === oldN - 2) {
    const newLastSegment = newSegments[oldN - 3];
    if (!newLastSegment) return marker;
    return remapOntoSegment(marker, 'end', distanceFromSegmentEnd(marker, oldSegment), oldN - 3, newLastSegment);
  }

  if (removeAt === 0 && seg === 0) {
    const newFirstSegment = newSegments[0];
    if (!newFirstSegment) return marker;
    return remapOntoSegment(marker, 'start', 0, 0, newFirstSegment);
  }

  return marker;
}

function remapMarkerAfterWaypointInsert(
  marker: PathMarker,
  insertAt: number,
  oldN: number,
  oldSegments: PathMeasureLike[],
  newSegments: PathMeasureLike[]
): PathMarker {
  const seg = marker.segmentIndex;

  if (insertAt === 0) {
    return { ...marker, segmentIndex: seg + 1 };
  }

  if (insertAt >= oldN) {
    return marker;
  }

  if (seg < insertAt - 1) {
    return marker;
  }

  if (seg > insertAt - 1) {
    return { ...marker, segmentIndex: seg + 1 };
  }

  const oldSegment = oldSegments[seg];
  const leftSegment = newSegments[insertAt - 1];
  const rightSegment = newSegments[insertAt];
  if (!oldSegment || !leftSegment || !rightSegment) return marker;

  const distanceFromStart = distanceFromSegmentStart(marker, oldSegment);
  const leftLength = leftSegment.getTotalLength();

  if (distanceFromStart <= leftLength) {
    return remapOntoSegment(marker, 'start', distanceFromStart, insertAt - 1, leftSegment);
  }

  return remapOntoSegment(marker, 'start', distanceFromStart - leftLength, insertAt, rightSegment);
}

function absoluteDistanceAlongPath(marker: PathMarker, waypoints: Waypoint[]): number {
  const segments = measureWaypointSegments(waypoints);
  let distance = 0;
  for (let i = 0; i < marker.segmentIndex && i < segments.length; i++) {
    distance += segments[i].getTotalLength();
  }
  const segment = segments[marker.segmentIndex];
  if (!segment) return distance;
  return distance + (markerLocalDistance(marker, segment) ?? 0);
}

function remapMarkerToAbsoluteDistance(marker: PathMarker, absoluteDistance: number, waypoints: Waypoint[]): PathMarker {
  const segments = measureWaypointSegments(waypoints);
  if (segments.length === 0) return marker;

  let remaining = Math.max(0, absoluteDistance);
  for (let segmentIndex = 0; segmentIndex < segments.length; segmentIndex++) {
    const segmentLength = segments[segmentIndex].getTotalLength();
    if (remaining <= segmentLength || segmentIndex === segments.length - 1) {
      const placement = markerFromAnchoredDistance('start', remaining, segments[segmentIndex], marker.type);
      return { ...marker, segmentIndex, ...placement };
    }
    remaining -= segmentLength;
  }

  const lastIndex = segments.length - 1;
  const placement = markerFromAnchoredDistance('start', segments[lastIndex].getTotalLength(), segments[lastIndex], marker.type);
  return { ...marker, segmentIndex: lastIndex, ...placement };
}

function syncMarkersAfterBulkWaypointEdit(oldWps: Waypoint[], newWps: Waypoint[], markers: PathMarker[]): PathMarker[] {
  if (newWps.length < 2) return [];
  return markers.map((marker) => {
    const absoluteDistance = absoluteDistanceAlongPath(marker, oldWps);
    return remapMarkerToAbsoluteDistance(marker, absoluteDistance, newWps);
  });
}

/**
 * Reindex or reposition markers when waypoints change (insert/remove one waypoint).
 *
 * - Insert: markers on the split segment stay at the same anchored distance along the path,
 *   placed on the left or right sub-segment; other markers shift as needed.
 * - Remove: markers are kept. Unaffected segments keep their index/value. Markers on
 *   segments that touch the removed waypoint move onto the merged/replacement segment
 *   while preserving their anchored distance from the surviving waypoint at that end
 *   (start-anchored for the segment before the removed waypoint, end-anchored after).
 */
export function syncMarkersAfterWaypointEdit(oldWps: Waypoint[], newWps: Waypoint[], markers: PathMarker[]): PathMarker[] {
  const oldN = oldWps.length;
  const newN = newWps.length;

  if (newN === oldN) return markers;

  /** Find the first position where the two waypoint arrays diverge. */
  const findDivergence = (shorter: Waypoint[], longer: Waypoint[]): number => {
    for (let j = 0; j < shorter.length; j++) {
      const a = shorter[j];
      const b = longer[j];
      if (a.x !== b.x || a.y !== b.y || a.rad !== b.rad || a.rot !== b.rot) return j;
    }
    return shorter.length;
  };

  if (newN === oldN + 1) {
    const insertAt = findDivergence(oldWps, newWps);
    const oldSegments = measureWaypointSegments(oldWps);
    const newSegments = measureWaypointSegments(newWps);

    return markers.map((marker) => remapMarkerAfterWaypointInsert(marker, insertAt, oldN, oldSegments, newSegments));
  }

  if (newN === oldN - 1) {
    if (newN < 2) return [];

    const removeAt = findDivergence(newWps, oldWps);
    const oldSegments = measureWaypointSegments(oldWps);
    const newSegments = measureWaypointSegments(newWps);

    return markers.map((marker) => remapMarkerAfterWaypointRemoval(marker, removeAt, oldN, oldSegments, newSegments));
  }

  // Multiple waypoints changed at once — preserve markers by anchored distance along the path.
  return syncMarkersAfterBulkWaypointEdit(oldWps, newWps, markers);
}

/**
 * Retrieve the SVG <path> element for a specific path segment from the DOM.
 */
export function getSegmentPathElement(svg: SVGSVGElement, pathId: string, segmentIndex: number): SVGPathElement | null {
  const segmentPaths = svg.querySelectorAll<SVGPathElement>('path[data-path-builder-segment]');
  for (const pathEl of segmentPaths) {
    if (pathEl.dataset.pathId !== pathId) continue;
    if (String(pathEl.dataset.segmentIndex) !== String(segmentIndex)) continue;
    return pathEl;
  }
  return null;
}

/**
 * Find the closest marker to a click point within a given radius (in SVG/meters units).
 * Returns null if no marker falls within the radius.
 */
export function findMarkerNearPoint(
  markers: PathMarker[],
  point: { x: number; y: number },
  radiusCm: number,
  svg: SVGSVGElement | null,
  pathId: string
): PathMarker | null {
  if (!svg || markers.length === 0) return null;
  let closestMarker: PathMarker | null = null;
  let closestDist = radiusCm;
  for (const marker of markers) {
    const segEl = getSegmentPathElement(svg, pathId, marker.segmentIndex);
    if (!segEl) continue;
    const { x, y } = computeMarkerPosition(marker, segEl);
    const dist = Math.hypot(point.x - x, point.y - y);
    if (dist < closestDist) {
      closestDist = dist;
      closestMarker = marker;
    }
  }
  return closestMarker;
}

/**
 * Convert a marker value from one placement type to another.
 * Requires the segment's SVG element to derive total arc length.
 * If the element is unavailable, returns the original value unchanged.
 */
/** Clamp a marker value to the valid range for its placement type and segment length. */
export function clampMarkerValue(type: PathMarkerType, value: number, segment: PathMeasureLike | null): number {
  const segmentLength = segment?.getTotalLength() ?? 0;
  if (type === PATH_MARKER_TYPES.SEGMENT_PERCENT) {
    return Math.round(Math.max(0, Math.min(100, value)));
  }
  const localDistance = type === PATH_MARKER_TYPES.DISTANCE_FROM_START ? value : segmentLength - value;
  return markerValueFromLocalDistance(localDistance, segmentLength, type);
}

export function convertMarkerValue(fromType: PathMarkerType, toType: PathMarkerType, value: number, segmentEl: SVGPathElement | null): number {
  if (fromType === toType) return value;
  const segmentLength = segmentEl ? segmentEl.getTotalLength() : 0;

  if (segmentLength <= 0) {
    return toType === PATH_MARKER_TYPES.SEGMENT_PERCENT ? 50 : 0;
  }

  let distFromStart: number;
  if (fromType === PATH_MARKER_TYPES.SEGMENT_PERCENT) {
    distFromStart = (value / 100) * segmentLength;
  } else if (fromType === PATH_MARKER_TYPES.DISTANCE_FROM_START) {
    distFromStart = value;
  } else {
    distFromStart = segmentLength - value;
  }

  const clampedDistFromStart = Math.max(0, Math.min(segmentLength, distFromStart));
  return markerValueFromLocalDistance(clampedDistFromStart, segmentLength, toType);
}

/**
 * Validate a proposed marker name: must be non-empty and unique among the
 * other markers on the path (excluding the marker being edited, by id).
 */
/** Deep-clone path markers for undo snapshots. */
export function clonePathMarkers(markers: PathMarker[]): PathMarker[] {
  return markers.map((marker) => ({
    ...marker,
    action: marker.action
      ? {
          ...marker.action,
          argValues: { ...marker.action.argValues },
        }
      : undefined,
  }));
}

export function validateMarkerName(name: string, markers: PathMarker[], currentMarkerId: string): boolean {
  if (!name.trim()) return false;
  return !markers.some((m) => m.id !== currentMarkerId && m.name === name.trim());
}

/**
 * Given a marker and segment geometry, compute the (x, y) position on the curve.
 */
function computeMarkerPositionOnSegment(marker: PathMarker, segment: PathMeasureLike): { x: number; y: number } {
  const localDistance = markerLocalDistance(marker, segment);
  if (localDistance == null) return { x: 0, y: 0 };

  const segmentLength = segment.getTotalLength();
  const clamped = Math.max(0, Math.min(segmentLength, localDistance));
  const pt = segment.getPointAtLength(clamped);
  return { x: pt.x, y: pt.y };
}

/** Compute marker (x, y) from path waypoints without relying on mounted SVG refs. */
export function computeMarkerPositionFromWaypoints(marker: PathMarker, waypoints: Waypoint[]): { x: number; y: number } {
  const segments = measureWaypointSegments(waypoints);
  const segment = segments[marker.segmentIndex];
  if (!segment) return { x: 0, y: 0 };
  return computeMarkerPositionOnSegment(marker, segment);
}

/**
 * Given a list of markers and a segment path element, compute the (x, y)
 * position on the curved segment for rendering.
 */
export function computeMarkerPosition(marker: PathMarker, segmentPathEl: SVGPathElement): { x: number; y: number } {
  return computeMarkerPositionOnSegment(marker, segmentPathEl);
}
